import React, { useCallback, useMemo, useState } from 'react'
import IconButton from '@material-ui/core/IconButton'
import CircularProgress from '@material-ui/core/CircularProgress'
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined'
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore'
import useFleetJobChannels from 'Hooks/useFleetJobChannels'
import { aggregateBlenderKpis, formatFleetValue, isUnitBillable } from '../fleetLiveHelpers'
import { fieldOverrideKey } from '../fleetHistoryHelpers'
import FleetSparkline from './FleetSparkline'
import FleetChannelsSlidePanel from './FleetChannelsSlidePanel'

const SERIES_COLORS = ['#3fc6e8', '#5ea8ff', '#7c8cf0', '#46d3c0', '#4a8ef0', '#f0a34a', '#e86fa8', '#9bc53d']

const FIELD_DECIMALS = {
  treatingpressure: 0,
  proppantstagetotal: 0,
}

const decimalsFor = (fieldName) =>
  FIELD_DECIMALS[String(fieldName || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()] ?? 1

const displayUnit = (unit) => (unit && unit !== '-' ? unit : '')

const getBlenderDashboardFields = (unit) => (Array.isArray(unit?.fields) ? unit.fields : [])
  .filter((f) => f?.fieldName && f.isDashboardField)
  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

const isOverridden = (field) => Boolean(field?.isCustom && field.channelId != null)

const fieldDecimals = (field) => (isOverridden(field) ? 1 : decimalsFor(field.fieldName))

/** First tile of the blender (the one with channel selection), as the tile shows it. */
export const getBlenderPrimaryReading = (unit) => {
  const field = getBlenderDashboardFields(unit)[0]
  if (!field) return null
  return {
    value: formatFleetValue(field.value, fieldDecimals(field)),
    unit: displayUnit(field.unit),
  }
}

/**
 * Dashboard fields by order. An overridden field stays in place (isCustom + channelId);
 * its value / unit / history already come from the picked channel.
 */
const buildBlenderItems = (unit, unitHistory, channelLabels, pendingByField) => {
  const dashboard = getBlenderDashboardFields(unit)

  const items = dashboard.length
    ? dashboard.map((f) => {
      const isCustom = isOverridden(f)
      return {
        key: f.fieldName,
        fieldName: f.fieldName,
        label: f.fieldName,
        value: f.value,
        unit: displayUnit(f.unit ?? unitHistory[f.fieldName]?.unit),
        decimals: fieldDecimals(f),
        points: unitHistory[f.fieldName]?.data,
        isCustom,
        channelId: isCustom ? f.channelId : null,
        channelName: isCustom ? f.channelName : null,
        channelLabel: isCustom ? channelLabels(f.channelId, f.channelName) : null,
        pending: pendingByField[f.fieldName] || null,
      }
    })
    // Legacy payload without dashboard fields — keep the fixed KPI set (no channel picking)
    : aggregateBlenderKpis([unit]).map((kpi) => ({
      key: `kpi:${kpi.name}`,
      label: kpi.name,
      formatted: kpi.value,
      unit: kpi.unit,
    }))

  return items.map((item, index) => ({
    ...item,
    color: SERIES_COLORS[index % SERIES_COLORS.length],
    points: item.points || [],
  }))
}

const BlenderCard = ({
  unit,
  unitHistory,
  pendingFieldChannels,
  jobIds,
  onSetFieldChannel,
  onResetFieldChannel,
  translation,
}) => {
  // Field whose channel panel is open
  const [panelFieldName, setPanelFieldName] = useState(null)

  const pendingByField = useMemo(() => {
    const result = {}
    ;(unit.fields || []).forEach((f) => {
      const pending = f?.fieldName && pendingFieldChannels[fieldOverrideKey(unit.unitUId, f.fieldName)]
      if (pending) result[f.fieldName] = pending
    })
    return result
  }, [unit.fields, unit.unitUId, pendingFieldChannels])

  const hasOverrides = (unit.fields || []).some(isOverridden)
  // Same cached query as the channels panel — localized names of the picked channels
  const { unit: jobUnit, units: jobUnits } = useFleetJobChannels(
    jobIds,
    unit.unitUId,
    hasOverrides || Boolean(panelFieldName),
    unit.unitName,
  )

  // Channel of another unit is prefixed with that unit's name
  const channelLabels = useCallback((channelId, channelName) => {
    const sourceUnit = jobUnits.find((u) => (u.channels || []).some((c) => c.id === channelId))
    const channel = sourceUnit?.channels.find((c) => c.id === channelId)
    const label = channel?.nameLocalized || channelName
    return sourceUnit && sourceUnit !== jobUnit
      ? `${sourceUnit.nameLocalized || sourceUnit.name} · ${label}`
      : label
  }, [jobUnits, jobUnit])

  const items = useMemo(
    () => buildBlenderItems(unit, unitHistory, channelLabels, pendingByField),
    [unit, unitHistory, channelLabels, pendingByField]
  )

  const panelItem = items.find((item) => item.fieldName && item.fieldName === panelFieldName) || null
  const panelSelectedChannelId = panelItem?.pending ? panelItem.pending.channelId : panelItem?.channelId

  const channelsTitle = (fieldName) => (
    (translation?.fleetChannelsPanelTitleFor || 'Select {name} channel').replace('{name}', fieldName)
  )
  const resetLabel = translation?.fleetResetChannelLabel || 'Reset to default'
  const billable = isUnitBillable(unit)

  return (
    <div className="pageBlock">
      <div
        className={`fdBlenderBlock${!billable ? ' fdBlenderBlock--unbillable' : ''}`}
        title={!billable ? (translation?.fleetUnitNotBillableLabel || 'Not in subscription') : undefined}
      >
        {unit.unitName ? (
          <div className="fdBlenderBlockTitle">{unit.unitName}</div>
        ) : null}
        <ul className="digitalDisplayList fdBlenderList">
          {items.map((item, index) => (
            <li
              className={`digitalDisplayListItem fdBlenderItem${item.isCustom ? ' fdBlenderItem--custom' : ''}`}
              key={item.key}
            >
              <h3 className="name" title={item.isCustom ? `${item.label} · ${item.channelLabel}` : item.label}>
                <span className="fdBlenderItemLabel">{item.label}</span>
                {item.pending ? (
                  <CircularProgress size={12} className="fdBlenderItemPending" />
                ) : null}
                {/* Channel change sits on the first blender field only */}
                {index === 0 && item.fieldName && billable ? (
                  <IconButton
                    size="small"
                    className="fdBlenderAddBtn"
                    title={channelsTitle(item.fieldName)}
                    aria-label={channelsTitle(item.fieldName)}
                    onClick={() => setPanelFieldName(item.fieldName)}
                  >
                    <SettingsOutlinedIcon fontSize="inherit" />
                  </IconButton>
                ) : null}
              </h3>
              {item.isCustom ? (
                <div className="fdBlenderItemSource" title={item.channelName}>
                  <span className="fdBlenderItemSourceName">{item.channelLabel}</span>
                  <IconButton
                    size="small"
                    className="fdBlenderResetBtn"
                    title={resetLabel}
                    aria-label={resetLabel}
                    disabled={Boolean(item.pending)}
                    onClick={() => onResetFieldChannel?.(unit.unitUId, item.fieldName)}
                  >
                    <SettingsBackupRestoreIcon fontSize="inherit" />
                  </IconButton>
                </div>
              ) : null}
              <div className="displayContent">
                <div className="data">
                  <span className="qnt">{item.formatted ?? formatFleetValue(item.value, item.decimals)}</span>
                  {item.unit}
                </div>
                {item.points.length > 1 ? (
                  <FleetSparkline
                    points={item.points}
                    color={item.color}
                    unit={item.unit}
                    decimals={item.decimals}
                  />
                ) : null}
                <div className="statusbar" style={{ backgroundColor: item.color }} />
              </div>
            </li>
          ))}
        </ul>
      </div>
      <FleetChannelsSlidePanel
        open={Boolean(panelItem)}
        onClose={() => setPanelFieldName(null)}
        jobIds={jobIds}
        unitUId={unit.unitUId}
        unitName={unit.unitName}
        title={panelItem ? channelsTitle(panelItem.fieldName) : undefined}
        selectedChannelId={panelSelectedChannelId ?? null}
        canReset={Boolean(panelItem?.isCustom)}
        onApply={(channelId, label) => onSetFieldChannel?.(unit.unitUId, panelItem.fieldName, channelId, label)}
        onReset={() => onResetFieldChannel?.(unit.unitUId, panelItem.fieldName)}
        translation={translation}
      />
    </div>
  )
}

const EMPTY = {}
const EMPTY_IDS = []

// Blender job first, so the blender's own unit is matched there before the MPC job
const resolveChannelJobIds = (jobPairs, licenseId) => {
  const pairs = Object.values(jobPairs)
  const pair = jobPairs[licenseId] || (pairs.length === 1 ? pairs[0] : null)
  return pair ? [pair.blenderJobId, pair.mpcJobId].filter((id) => id != null) : EMPTY_IDS
}

const BlenderTiles = ({
  blenders = [],
  history = EMPTY,
  jobPairs = EMPTY,
  pendingFieldChannels = EMPTY,
  onSetFieldChannel,
  onResetFieldChannel,
  translation,
}) => {
  if (!blenders.length) {
    const kpis = aggregateBlenderKpis([])
    return (
      <div className="pageBlock">
        <div className="fdBlenderBlock">
          <ul className="digitalDisplayList fdBlenderList">
            {kpis.map((kpi) => (
              <li className="digitalDisplayListItem fdBlenderItem" key={kpi.name}>
                <h3 className="name">{kpi.name}</h3>
                <div className="displayContent">
                  <div className="data">
                    <span className="qnt">{kpi.value}</span>
                    {kpi.unit}
                  </div>
                  <div className="statusbar" style={{ backgroundColor: kpi.color }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )
  }

  return blenders.map((unit) => (
    <BlenderCard
      key={unit.unitUId}
      unit={unit}
      unitHistory={history[unit.unitUId] || EMPTY}
      pendingFieldChannels={pendingFieldChannels}
      jobIds={resolveChannelJobIds(jobPairs, unit.licenseId)}
      onSetFieldChannel={onSetFieldChannel}
      onResetFieldChannel={onResetFieldChannel}
      translation={translation}
    />
  ))
}

export default BlenderTiles
